import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  try {
    const stored = localStorage.getItem('axis-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      const token = parsed?.state?.session?.access_token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (e) {
    // ignore
  }
  return config;
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const currentPath = window.location.pathname;

    if (error.response?.status === 401 && !originalRequest._retry && currentPath !== '/auth' && currentPath !== '/onboard') {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const stored = localStorage.getItem('axis-auth');
          const parsed = stored ? JSON.parse(stored) : null;
          const refreshToken = parsed?.state?.session?.refresh_token;

          if (!refreshToken) {
            throw new Error('No refresh token');
          }

          const { data } = await axios.post('/api/auth/refresh', { refresh_token: refreshToken });

          const newSession = data.session;
          if (parsed) {
            parsed.state.session = newSession;
            parsed.state.user = data.user;
            localStorage.setItem('axis-auth', JSON.stringify(parsed));
          }

          isRefreshing = false;
          onTokenRefreshed(newSession.access_token);

          // Sync Zustand store so React reads the fresh token without a reload
          try {
            window.dispatchEvent(new CustomEvent('axis:session-refreshed', {
              detail: { session: newSession, user: data.user },
            }));
          } catch (err) { console.error('[api] session-refreshed event dispatch failed:', err); }

          originalRequest.headers.Authorization = `Bearer ${newSession.access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = [];
          localStorage.removeItem('axis-auth');
          window.location.href = '/auth';
          return Promise.reject(refreshError);
        }
      }

      return new Promise((resolve) => {
        addRefreshSubscriber((token: string) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(api(originalRequest));
        });
      });
    }

    if (error.response?.status === 401 && currentPath !== '/auth' && currentPath !== '/onboard') {
      localStorage.removeItem('axis-auth');
      window.location.href = '/auth';
    }

    return Promise.reject(error);
  }
);

export default api;
